import React, { useState } from 'react';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { CardSkeleton } from 'src/modules/shared/components/atoms/cards/card-skeleton';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import { DateInput } from 'src/modules/shared/components/atoms/inputs/date-input';
import {
  TagDropdown,
  TagDropdownOption,
} from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import { AssigneeDropdown } from 'src/modules/shared/components/molecules/dropdowns/assignee-dropdown';
import {
  PiCircleLight,
  PiXLight,
  PiCheckLight,
  PiCalendar,
  PiCalendarSlash,
} from 'react-icons/pi';
import Loading from 'src/modules/shared/components/atoms/loading';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { getCurrentDateTimeLocal } from 'src/modules/shared/utils/formatting/date.util';
import { useTaskOperations } from '../../../hooks/tasks/use-task-operations';
/**
 * Interface for the NewTaskForm component props
 */
interface NewTaskFormProps {
  /** Callback function triggered when a task is successfully created */
  onTaskCreated?: () => void;
  /** Callback function triggered when a task is cancelled */
  onTaskCancel?: () => void;

  /** ID of the conversation to associate with the task */
  conversationId?: number;
  /** Whether to show the conversation selection field */
  showConversationField?: boolean;

  /** Placeholder text for the task input field */
  placeholder?: string;
  /** Whether to automatically focus the input field when component mounts */
  autoFocus?: boolean;
  /** Additional CSS class names to apply to the component */
  className?: string;
}

export const NewTaskForm: React.FC<NewTaskFormProps> = ({
  onTaskCreated = () => {},
  onTaskCancel = () => {},
  conversationId,
  showConversationField = false,
  // TODO: check when implementing i18n
  placeholder = 'Nueva tarea...',
  autoFocus = true,
  className = '',
}) => {
  const [isCreating, setIsCreating] = useState(true);
  const [objective, setObjective] = useState('');
  const [hasDueDate, setHasDueDate] = useState(false);
  const [dueDate, setDueDate] = useState(getCurrentDateTimeLocal());
  const [assignee, setAssignee] = useState<
    { type: 'user' | 'ai_employee'; id: string } | undefined
  >(undefined);
  const [selectedConversationId, setSelectedConversationId] = useState<
    number | undefined
  >(conversationId);

  const { handleCreateTask, creatingTask, currentUser } = useTaskOperations();

  React.useEffect(() => {
    if (!currentUser || assignee) return;

    setAssignee({
      type: 'user',
      id: String(currentUser.id),
    });
  }, [currentUser, assignee]);

  const getConversationOptions = (): TagDropdownOption[] => {
    const options: TagDropdownOption[] = [
      {
        id: 'none',
        // TODO: check when implementing i18n
        content: <Text size="small">Sin conversación</Text>,
        selected: !selectedConversationId,
      },
    ];

    return options;
  };

  const handleConversationChange = (option: TagDropdownOption) => {
    if (option.id === 'none') {
      setSelectedConversationId(undefined);
    } else {
      setSelectedConversationId(Number(option.id));
    }
  };

  const getDisplayConversation = () => {
    // TODO: check when implementing i18n
    if (!selectedConversationId) return 'Sin conversación';
    // TODO: check when implementing i18n
    return `Conversación ${selectedConversationId}`;
  };

  const handleToggleDueDate = () => {
    if (!hasDueDate) {
      setDueDate(getCurrentDateTimeLocal());
    }
    setHasDueDate(!hasDueDate);
  };

  const resetForm = () => {
    setObjective('');
    setHasDueDate(false);
    setDueDate(getCurrentDateTimeLocal());
    setAssignee(undefined);
    setSelectedConversationId(conversationId);
    setIsCreating(false);
  };

  const handleSubmit = async () => {
    if (!objective.trim() || !assignee) {
      return;
    }

    await handleCreateTask({
      objective,
      dueDate: hasDueDate ? dueDate : null,
      assignee,
      conversationId: selectedConversationId,
    });

    resetForm();

    onTaskCreated();
  };

  const handleCancel = () => {
    resetForm();

    onTaskCancel();
  };

  if (!isCreating) return null;

  if (!currentUser) {
    return (
      <CardSkeleton
        variant="secondary"
        className={`px-2 py-0.5 ${className}`}
        content={{ lines: 1, widths: ['w-full'], heights: ['h-8'] }}
      />
    );
  }

  return (
    <Card
      variant="neutral"
      appearance="outline"
      size="sm"
      shadow="none"
      className={`px-2 py-0.5 relative ${className}`}
    >
      {creatingTask && (
        <div className="absolute inset-0 bg-white/80 rounded-lg flex items-center justify-center z-10">
          <Loading size="sm" variant="primary" />
        </div>
      )}

      <div className="flex py-1 items-center gap-2 flex-wrap justify-end">
        {/* Status Icon */}
        <div className="flex-shrink-0 mr-auto">
          <PiCircleLight className="h-5 w-5 text-gray-300" />
        </div>

        {/* Objective Input */}
        <div className="flex-1 min-w-[180px] pl-2">
          <TextInput
            value={objective}
            onChange={(e) => setObjective(e.target.value)}
            placeholder={placeholder}
            size="sm"
            variant="primary"
            className="w-full"
            disabled={creatingTask}
            autoFocus={autoFocus}
          />
        </div>

        {/* Date Input - Only shown when hasDueDate is true */}
        <div className="flex flex-shrink-0 items-center whitespace-nowrap">
          <Tooltip
            // TODO: check when implementing i18n
            content={
              <Text size="small">
                {hasDueDate ? 'Quitar fecha límite' : 'Agregar fecha límite'}
              </Text>
            }
            placement="top"
            size="xs"
          >
            <Button
              onClick={handleToggleDueDate}
              variant={hasDueDate ? 'neutral' : 'primary'}
              appearance="soft"
              size="sm"
              isIconOnly
              disabled={creatingTask}
              className="flex-shrink-0"
            >
              {hasDueDate ? (
                <PiCalendarSlash className="h-4 w-4" />
              ) : (
                <PiCalendar className="h-4 w-4" />
              )}
            </Button>
          </Tooltip>

          {hasDueDate && (
            <DateInput
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              type="datetime-local"
              size="sm"
              variant="primary"
              disabled={creatingTask}
              containerClassName="flex"
            />
          )}
        </div>

        {/* Conversation Selector - shown only when showConversationField is true */}
        {showConversationField && (
          <div className="flex flex-shrink-0 items-center whitespace-nowrap">
            <TagDropdown
              content={
                <Text size="tiny" className="truncate block w-full">
                  {getDisplayConversation()}
                </Text>
              }
              variant="primary"
              appearance="outline"
              size="xs"
              options={getConversationOptions()}
              onOptionSelect={handleConversationChange}
              disabled={creatingTask}
            />
          </div>
        )}

        {/* Action Area */}
        <div className="flex flex-shrink-0 items-center whitespace-nowrap gap-2">
          {/* Assignee Dropdown */}
          <AssigneeDropdown
            value={assignee}
            onAssigneeChange={async (newAssignee) => setAssignee(newAssignee)}
            disabled={creatingTask}
            size="xs"
            placeholder="Asignar tarea"
          />

          {/* Action Buttons */}
          <div className="flex flex-shrink-0 items-center whitespace-nowrap gap-1">
            <Button
              size="sm"
              variant="neutral"
              appearance="ghost"
              onClick={handleCancel}
              isIconOnly
              disabled={creatingTask}
            >
              <PiXLight className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={handleSubmit}
              disabled={!objective.trim() || creatingTask || !assignee}
              isIconOnly
            >
              <PiCheckLight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
